import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetUserQuery, useGetUserSalesPointsQuery } from 'features/getUsers/api/getUsersApi';
import { StyledTab, StyledTabList, StyledTabPanel } from 'shared/styles/StyledTabs';
import TabContext from '@mui/lab/TabContext';
import { TableContent } from 'entities/table/ui/TableContent';
import {
  Drawer as DrawerCreate,
  Box,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Breadcrumbs,
  Link,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import { UserInfo } from 'entities/user/ui/UserInfo';
import { DrawerContent } from 'shared/ui/Drawer/DrawerStyles';
import { useSyncState } from 'shared/hooks/useSyncState';
import { useAppDispatch, useAppSelector } from 'app/store';
import { setHeaderTitle, setHeaderAction, setActionBtnTitle, setDrawerOpen } from 'features/ui/model/uiSlice';
import { SendPersonalNotification } from 'features/sendPersonalNotification/ui/SendPersonalNotification';
import { StyledCard } from 'shared/ui/Card/StyledCard';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { ResetPassword } from 'features/changeUserData/ui/ResetPassword';
import { ChangeStatus } from 'features/changeUserData/ui/ChangeStatus';
import { useGetNotificationsQuery } from 'features/userNotifications';
import moment from 'moment';
import {
  useGetProfileWalletBalanceQuery,
  useGetTransactionsQuery,
  useGetUserBalanceQuery,
} from 'features/userTransactions/api';
import { isEmptyArr } from 'shared/utils/isEmpty';
import { UpdateUserCardTabs } from 'entities/users/ui/UpdateUserCardTabs';
import { DataTableBackendPagination } from 'shared/ui/Table';
import { debounce } from 'lodash';
import { UserProfileSalePoint } from 'entities/user/model/types';
import { useGetCitiesQuery } from 'features/getCities/api/getCitiesApi';

type FilterSalesPointsType = {
  search: string;
};

const statuses = [
  { value: 'success', title: 'Успех' },
  { value: 'fail', title: 'Ошибка' },
  { value: 'in_progress', title: 'В процессе' },
  { value: 'init', title: 'Инициализация' },
];

const types = [
  { value: 'withdraw', title: 'Вывод' },
  { value: 'deposit', title: 'Пополнение' },
];

const getStatus = (status: string): string => {
  const res = statuses.find((item) => item.value === status);
  return res ? res.title : '-----';
};

const getType = (status: string): string => {
  const res = types.find((item) => item.value === status);
  return res ? res.title : '-----';
};

export const UserPage = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentLocation = useLocation().pathname;
  const pathnameArray = currentLocation.split('/');
  const { isDrawerOpen } = useAppSelector((state) => state.ui);
  const [tab, setTab] = useState('1');
  const [drawerVariant, setDrawerVariant] = useState<{
    variant: 'create' | 'edit' | 'notification' | 'reset-password' | 'change-status';
    title: string;
  }>({
    variant: 'edit',
    title: 'Изменить данные',
  });
  const { data: citiesList } = useGetCitiesQuery({ sort: 'country_id,name_ru' });
  const [pagination, setPagination] = useState<{
    page: number;
    count: number;
    search: string;
  }>({
    page: 1,
    count: 10,
    search: '',
  });
  const [transPagination, setTransPagination] = useState<{
    page: number;
    count: number;
    search: string;
  }>({
    page: 1,
    count: 10,
    search: '',
  });
  const [salesPointsPagination, setSalesPointsPagination] = useState<{
    page: number;
    rowsPerPage: number;
  }>({
    page: 0,
    rowsPerPage: 10,
  });
  const [filterSalesPoints, setFilterSalesPoints] = useState<FilterSalesPointsType>({
    search: '',
  });
  const searchRef = useRef('');
  const searchQuery = useSyncState('');
  const [isShowSearch, setIsShowSearch] = useState(false);
  const { data: user, isSuccess: isProfileSuccess } = useGetUserQuery(id!);
  const { data: notifications, isSuccess: isNotificationSuccess } = useGetNotificationsQuery({
    user_id: +id!,
    page: pagination.page,
    size: pagination.count,
    search: pagination.search,
  });
  const { data: salesPoints } = useGetUserSalesPointsQuery({
    user_id: +id!,
    page: salesPointsPagination.page + 1,
    size: salesPointsPagination.rowsPerPage,
    ...filterSalesPoints,
  });
  const { data: transactions, isSuccess: isTransactionSuccess } = useGetTransactionsQuery({
    user_id: +id!,
    page: pagination.page,
    size: pagination.count,
    search: pagination.search,
    app_type: pathnameArray[2],
  });
  const { data: balance, isSuccess } = useGetUserBalanceQuery({ user_id: +id!, app_type: pathnameArray[2] });
  const { data: profileWalletBalance, isSuccess: isSuccessProfileWalletBalance } = useGetProfileWalletBalanceQuery({
    user_id: +id!,
    wallet_app_type: pathnameArray[2],
  });

  const [profileActionsAnchorEl, setProfileActionsAnchorEl] = useState<null | HTMLElement>(null);
  const isOpenProfileActionMenu = Boolean(profileActionsAnchorEl);
  const handleProfileActionsMenu = (e: React.MouseEvent<HTMLElement>) => {
    setProfileActionsAnchorEl(e.currentTarget);
  };
  const handleCloseProfileActionsMenu = () => {
    setProfileActionsAnchorEl(null);
  };

  const handleChangeTab = (_: React.SyntheticEvent, newTab: string) => setTab(newTab);

  const onDrawerClose = () => {
    dispatch(setDrawerOpen(false));
  };
  const handleClearSearch = () => {
    setIsShowSearch(false);
    searchQuery.set('');
    searchRef.current = '';
    setPagination((prev) => ({
      ...prev,
      search: '',
    }));
  };
  const onSalesPointsSearch = debounce((search) => {
    setFilterSalesPoints({ ...filterSalesPoints, search });
    setSalesPointsPagination({ ...salesPointsPagination, page: 0 });
  }, 300);
  const handleSetSearch = (search: string) => {
    searchQuery.set(search);
    searchRef.current = search;
  };
  useEffect(() => {
    dispatch(setHeaderAction('edit'));
    dispatch(setActionBtnTitle('Изменить данные'));

    return () => {
      dispatch(setHeaderAction(null));
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setHeaderTitle(`${user?.last_name ?? '---'} ${user?.first_name ?? '---'} ${user?.middle_name ?? '---'}`));
  }, [isProfileSuccess, user, dispatch]);

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          margin: '0 0 4px 0',
        }}
      >
        <Link
          color="inherit"
          href="#"
        >
          Пользователи
        </Link>
        <Link
          color="inherit"
          href="#"
        >
          Пользователи МП
        </Link>
        <Link
          color="inherit"
          href={`/users-mp/${pathnameArray[2]}`}
        >
          {pathnameArray[2]}
        </Link>
        {user && user?.first_name && user?.last_name && (
          <Link
            color="inherit"
            href="#"
          >
            {user && `${user?.first_name} ${user?.last_name}`}
          </Link>
        )}
      </Breadcrumbs>
      <TabContext value={tab}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '30px',
          }}
        >
          <StyledTabList onChange={handleChangeTab}>
            <StyledTab
              label="Личные данные"
              value="1"
              disableRipple
              onClick={() => {
                setDrawerVariant({
                  variant: 'edit',
                  title: 'Изменить данные',
                });
                dispatch(setHeaderAction('edit'));
                dispatch(setActionBtnTitle('Изменить данные'));
              }}
            />
            <StyledTab
              label="Кошелек"
              value="2"
              disableRipple
              onClick={() => {
                setDrawerVariant({
                  variant: 'edit',
                  title: 'Изменить данные',
                });
                dispatch(setHeaderAction('edit'));
                dispatch(setActionBtnTitle('Изменить данные'));
              }}
            />
            <StyledTab
              label="Уведомления"
              value="3"
              disableRipple
              onClick={() => {
                setDrawerVariant({
                  variant: 'notification',
                  title: 'Создать персональное уведомление',
                });
                dispatch(setHeaderAction('create'));
                dispatch(setActionBtnTitle('Создать'));
              }}
            />
            {user?.is_b2b && (
              <StyledTab
                label="Торговые точки"
                value="4"
                disableRipple
                onClick={() => {
                  setDrawerVariant({
                    variant: 'edit',
                    title: 'Изменить данные',
                  });
                  dispatch(setHeaderAction('edit'));
                  dispatch(setActionBtnTitle('Изменить данные'));
                }}
              />
            )}
          </StyledTabList>
          <Button
            sx={{
              textTransform: 'none',
            }}
            disableElevation
            endIcon={<MoreVertIcon />}
            onClick={handleProfileActionsMenu}
          >
            Активный пользователь
          </Button>
          <Menu
            anchorEl={profileActionsAnchorEl}
            open={isOpenProfileActionMenu}
            onClose={handleCloseProfileActionsMenu}
          >
            <MenuItem
              onClick={() => {
                setDrawerVariant({
                  variant: 'edit',
                  title: 'Изменить данные',
                });
                dispatch(setDrawerOpen(true));
                setProfileActionsAnchorEl(null);
              }}
            >
              Изменить данные
            </MenuItem>
            <MenuItem
              onClick={() => {
                setDrawerVariant({
                  variant: 'reset-password',
                  title: 'Сброс пароля',
                });
                dispatch(setDrawerOpen(true));
                setProfileActionsAnchorEl(null);
              }}
            >
              Сбросить пароль
            </MenuItem>
            <MenuItem
              onClick={() => {
                setDrawerVariant({
                  variant: 'change-status',
                  title: 'Блокировка пользователя',
                });
                dispatch(setDrawerOpen(true));
                setProfileActionsAnchorEl(null);
              }}
            >
              {user?.status === 1 ? 'Заблокировать' : 'Разблокировать'}
            </MenuItem>
          </Menu>
        </Box>
        <StyledTabPanel value="1">
          <UserInfo
            profile={user}
            walletBalance={isSuccessProfileWalletBalance ? profileWalletBalance?.balance : undefined}
          />
        </StyledTabPanel>
        {isSuccess && (
          <StyledTabPanel value="2">
            <Box sx={{ display: 'flex', marginBottom: '24px' }}>
              <Box
                component={StyledCard}
                sx={{ marginRight: '32px' }}
              >
                <Box sx={{ display: 'flex', marginBottom: '12px' }}>
                  <PaymentsOutlinedIcon />
                  <Typography sx={{ paddingLeft: '8px' }}>Текущий баланс</Typography>
                </Box>
                <Typography>
                  <span style={{ fontSize: '24px' }}>
                    {(balance.filter((item: any) => item.type === 'bonus')[0].balance / 100)
                      .toLocaleString('en-US')
                      .replace(',', ' ')}
                  </span>{' '}
                  Бонусов
                </Typography>
              </Box>
            </Box>
            {isTransactionSuccess &&
              (!isEmptyArr(transactions.items) ? (
                <TableContent
                  fields={[
                    {
                      field: 'id',
                      title: 'ID',
                      width: '5%',
                    },
                    {
                      field: 'title',
                      title: 'Операция',
                      width: '15%',
                    },
                    {
                      field: 'summ',
                      title: 'Сумма операции',
                      width: '15%',
                    },
                    {
                      field: 'time',
                      title: 'Время операции',
                      width: '15%',
                    },
                    {
                      field: 'status',
                      title: 'Статус',
                      width: '25%',
                    },
                  ]}
                  list={{
                    list: transactions?.items.map((t) => ({
                      id: t?.id,
                      title: getType(t?.type),
                      summ: ((t?.amount || 0) / 100).toLocaleString('en-US').replace(',', ' '),
                      time: moment(t?.created_dttm).format('DD-MM-YYYY, HH:MM:ss'),
                      status: getStatus(t?.status),
                      additional: t?.description,
                    })),
                    total: transactions?.total,
                    page: transactions?.page,
                    size: transactions?.size,
                  }}
                  title="Транзакции пользователя"
                  count={transactions.total}
                  page={transPagination.page - 1}
                  perPage={transPagination.count}
                  onPageChange={(_, page) => {
                    setTransPagination((prev) => ({
                      ...prev,
                      page: page + 1,
                    }));
                  }}
                  onRowsPerPageChange={(e) => {
                    setTransPagination(() => ({
                      page: 1,
                      count: parseInt(e.target.value),
                      search: transPagination.search,
                    }));
                  }}
                  onRowClick={() => {}}
                />
              ) : (
                <Typography textAlign="center">В данный момент, отсутствуют данные по этой таблице</Typography>
              ))}
          </StyledTabPanel>
        )}
        <StyledTabPanel value="3">
          {isNotificationSuccess &&
            (!isEmptyArr(notifications?.items) ? (
              <TableContent
                fields={[
                  {
                    field: 'id',
                    title: 'ID',
                    width: '5%',
                  },
                  {
                    field: 'title',
                    title: 'Заголовок',
                    width: '25%',
                  },
                  {
                    field: 'content',
                    title: 'Содержание',
                    width: '25%',
                  },
                  {
                    field: 'sended',
                    title: 'Отправлено',
                    width: '35%',
                  },
                ]}
                list={{
                  list: notifications?.items.map((n) => ({
                    id: n?.id,
                    title: n?.title,
                    content: n?.content,
                    sended: moment(n?.created_dttm).format('DD-MM-YYYY, HH:MM:ss'),
                  })),
                  total: notifications.total,
                  page: notifications.page,
                  size: notifications.size,
                }}
                title="Полученные уведомления"
                isShowSearch={isShowSearch}
                onShowSearch={(showSearch) => setIsShowSearch(showSearch)}
                onClearSearch={handleClearSearch}
                search={searchQuery.get()}
                setSearch={handleSetSearch}
                count={notifications.total}
                page={pagination.page - 1}
                perPage={pagination.count}
                onPageChange={(_, page) => {
                  setPagination((prev) => ({
                    ...prev,
                    page: page + 1,
                  }));
                }}
                onRowsPerPageChange={(e) => {
                  setPagination(() => ({
                    page: 1,
                    count: parseInt(e.target.value),
                    search: pagination.search,
                  }));
                }}
                onRowClick={() => {}}
              />
            ) : (
              <Typography textAlign="center">В данный момент, отсутствуют данные по этой таблице</Typography>
            ))}
        </StyledTabPanel>

        <StyledTabPanel value="4">
          <DataTableBackendPagination
            columnConfig={[
              {
                dataFieldName: 'id',
                name: 'ID',
                width: '5%',
              },
              {
                dataFieldName: 'title',
                name: 'Наименование ТТ',
                width: '25%',
              },
              {
                dataFieldName: 'door_code',
                name: 'Код двери',
                width: '25%',
              },
              {
                dataFieldName: 'address',
                name: 'Адрес',
                width: '25%',
              },
              {
                dataFieldName: 'direction',
                name: 'Канал',
                width: '25%',
              },
            ]}
            data={
              salesPoints
                ? (salesPoints?.items as UserProfileSalePoint[]).map((salePoint) => ({
                    ...salePoint,
                    direction: salePoint.direction === 1 ? 'KAM' : salePoint.direction === 2 ? 'DS' : 'OP',
                    address: `г. ${citiesList?.find((city) => salePoint.city_id === city.id)?.name_ru}, ${
                      salePoint.address
                    }`,
                  }))
                : []
            }
            title="Торговые точки"
            search
            emptyListTitle="Данные не найдены"
            onSearch={onSalesPointsSearch}
            searchPlaceholder="Поиск по наименованию торговой точки"
            pagination={salesPointsPagination}
            total={salesPoints?.total || 0}
            setPagination={setSalesPointsPagination}
            onRowClick={(id) => navigate(`/shop-dots/${id}`)}
          />
        </StyledTabPanel>
      </TabContext>
      <DrawerCreate
        anchor="right"
        open={isDrawerOpen}
        onClose={onDrawerClose}
      >
        <DrawerContent>
          <div className="header">
            <Typography
              variant="h5"
              sx={{ fontWeight: '600' }}
            >
              {drawerVariant.title}
            </Typography>
            <IconButton
              onClick={onDrawerClose}
              sx={{ color: '#000' }}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div className="content">
            {drawerVariant.variant === 'edit' && isProfileSuccess && <UpdateUserCardTabs />}
            {drawerVariant.variant === 'reset-password' && isProfileSuccess && (
              <ResetPassword
                app_type={pathnameArray[2]}
                profile={user}
              />
            )}
            {drawerVariant.variant === 'change-status' && isProfileSuccess && <ChangeStatus profile={user} />}
            {drawerVariant.variant === 'notification' && (
              <SendPersonalNotification
                id={id!}
                app_type={pathnameArray[2]}
              />
            )}
          </div>
        </DrawerContent>
      </DrawerCreate>
    </>
  );
};
